import { DialogTitle, DialogContent, DialogActions, Dialog } from '@mui/material';
import { formatTime } from 'neuro-utils';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import ActionButton from 'Components/ActionButton';
import { Container, Item } from 'Components/Grid';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import { dialogTitle, dialogContent, dialogActions, dialogCancel } from '../../../../../config/theme/componentTheme';
import { actions } from 'Store/appointments';
import { actions as myActions } from 'Store/myapp/actions';
import { sortDocuments } from 'Utility/randomUtil';
import AppointmentForm from '../../Form/appointmentForm';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import colors from '../../../../../config/theme/colors';
import { EN_DASH } from '../../../../../config/constants';

/**
 * Appointments are not actual documents, but they reuse HistoryRowControls, which is typed for IDocumentV2.
 * Only the control fields set here are actually read by the controls.
 * 'appointment' is deliberately outside TDocumentType --> need to cast
 */
const asAppointmentPseudoDocument = (a: IAppointment): IDocumentV2 =>
  ({
    ...a,
    _id: a.id,
    _type: 'appointment',
    _ownerOrg: a.creatorOrg,
    _cdate: a.createdAt,
    _creator: a.creatorId,
    _docCreateDate: a.createdAt,
    _lockedFor: null,
    _source: null,
    _pastOwners: {},
  }) as unknown as IDocumentV2;

const formatHeaderText = (a: IAppointment): string => {
  return `${formatPartialDate(a.startDate)}${Boolean(a.startTime) ? ` (${formatTime(a.startTime)})` : ''}${
    a.endDate ? ` ${EN_DASH} ${formatPartialDate(a.endDate)}${a.endTime ? ` (${formatTime(a.endTime)})` : ''}` : ''
  }`;
};

const AppointmentList = (): React.JSX.Element => {
  const appointments = useAppSelector((s: IState) => s.appointments.appointments) ?? [];
  const taskLists = useAppSelector((s: IState) => s.myapp.taskLists);
  const [editingAppointment, setEditingAppointment] = React.useState<string | null>(null);
  const [appointmentData, setAppointmentData] = React.useState<Partial<IAppointment> | null>(null);

  const changeAppointmentData = (values: TOnChangeValues): void =>
    setAppointmentData({ ...appointmentData, ...values });

  const setEditingAppointments = (app: IAppointment) => (): void => {
    setEditingAppointment(app.id);
    setAppointmentData(appointments.find(a => a.id === app.id) ?? null);
  };

  const unsetEditingAppointments = (): void => {
    setEditingAppointment(null);
    setAppointmentData(null);
  };

  const dispatch = useAppDispatch();

  const saveAppointment = async (): Promise<void> => {
    unsetEditingAppointments();
    if (appointmentData) {
      const appoId = await actions.changeAppointmentData(appointmentData as IAppointment)(dispatch);
      if (appoId) {
        // If appointment is linked to a tasklist, fetch them again as backend updates the linked taskList also
        if (taskLists.some(tl => tl.appointment_id === appoId)) {
          await myActions.loadTaskLists(dispatch);
        }
      }
    }
  };

  const deleteAppointment = (id: string) => async (): Promise<void> => {
    await actions.deleteExistingAppointment(id)(dispatch);
    // If appointment is linked to a tasklist, fetch them again as backend updates the linked taskList also
    if (taskLists.some(tl => tl.appointment_id === id)) {
      await myActions.loadTaskLists(dispatch);
    }
  };

  const sortedAppointments = appointments.toSorted((n1, n2) =>
    sortDocuments([
      { type: 'date', sortField: 'startDate' },
      { type: 'time', sortField: 'startTime' },
    ])(n1, n2),
  );

  return (
    <>
      {sortedAppointments.map(a => (
        <HistoryRow
          key={a.id}
          headerText={formatHeaderText(a)}
          rowButton={
            <HistoryRowControls
              document={asAppointmentPseudoDocument(a)}
              startEdit={() => setEditingAppointments(a)}
              deleteContent={<FormattedMessage id="appointments.shallDelete" />}
              deleteHandler={deleteAppointment(a.id)}
            />
          }
          controlsMargin={false}
        >
          <>
            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.status" />}
              value={<FormattedMessage id={`appointments.opts.${a.status}`} />}
            />
            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.externalAppointmentType" />}
              value={a.externalAppointmentType}
            />

            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.locationGroup.title" />}
              value={
                a.locationGroupId || a.locationGroupName ? (
                  <Container>
                    <Item xs={4}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.locationGroup.id" />
                      </div>
                      {a.locationGroupId ?? '-'}
                    </Item>
                    <Item xs={7}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.locationGroup.name" />
                      </div>
                      {a.locationGroupName ?? '-'}
                    </Item>
                  </Container>
                ) : undefined
              }
            />
            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.locationType.title" />}
              value={
                a.locationTypeId || a.locationTypeText ? (
                  <Container>
                    <Item xs={4}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.locationType.id" />
                      </div>
                      {a.locationTypeId ?? '-'}
                    </Item>
                    <Item xs={7}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.locationType.text" />
                      </div>
                      {a.locationTypeText ?? '-'}
                    </Item>
                  </Container>
                ) : undefined
              }
            />
            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.location.title" />}
              value={
                a.locationId || a.locationName ? (
                  <Container>
                    <Item xs={4}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.location.id" />
                      </div>
                      {a.locationId ?? '-'}
                    </Item>
                    <Item xs={7}>
                      <div style={{ color: colors.darkGray, fontWeight: 400 }}>
                        <FormattedMessage id="appointments.location.name" />
                      </div>
                      {a.locationName ?? '-'}
                    </Item>
                  </Container>
                ) : undefined
              }
            />

            <HistoryRowVerticalItem
              header={<FormattedMessage id="appointments.comments" />}
              value={a.comments ?? undefined}
            />
          </>
        </HistoryRow>
      ))}
      <Dialog open={!!editingAppointment} maxWidth="lg" fullWidth={true} PaperProps={{ square: true }}>
        <DialogTitle style={dialogTitle}>
          <FormattedMessage id="appointments.editAppointment" />
        </DialogTitle>
        <DialogContent style={dialogContent}>
          <AppointmentForm data={appointmentData} changeData={changeAppointmentData} />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <Container alignItems="center">
            <Item xs={6}></Item>
            <Item xs={6}>
              <Container justifyContent="flex-end" alignItems="center">
                <Item>
                  <div style={dialogCancel} onClick={unsetEditingAppointments}>
                    <FormattedMessage id="general.cancel" />
                  </div>
                </Item>
                <Item>
                  <ActionButton text={'general.accept'} onClick={saveAppointment} width={15} height={4} fontSize={16} />
                </Item>
              </Container>
            </Item>
          </Container>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentList;
